/**
 * @fileoverview Floating selection action bar for Baselinker import.
 * @module features/baselinker-import/components/AddFromBaselinkerSelectionActionBar
 */

import { AnimatePresence, motion } from 'framer-motion'
import { Loader2, Plus } from 'lucide-react'
import { ReactElement, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'

export interface AddFromBaselinkerSelectionActionBarProps {
  /** Number of selected products */
  productCount: number
  /** Number of selected variants (individual, not via product) */
  variantCount: number
  /** Whether import is in progress */
  isImporting?: boolean
  /** Clears the current selection */
  onClearSelection: () => void
  /** Triggers the import action */
  onImport: () => void
}

/**
 * Floating selection bar displayed when products/variants are selected for import.
 *
 * @component
 */
export function AddFromBaselinkerSelectionActionBar({
  productCount,
  variantCount,
  isImporting = false,
  onClearSelection,
  onImport,
}: AddFromBaselinkerSelectionActionBarProps): ReactElement | null {
  const { t } = useTranslation('features-products')

  const totalCount = productCount + variantCount
  const hasSelection = totalCount > 0

  useEffect(() => {
    if (!hasSelection) return undefined

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClearSelection()
      }
      if (event.key === 'Enter' && !isImporting) {
        event.preventDefault()
        onImport()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [hasSelection, isImporting, onClearSelection, onImport])

  if (!hasSelection) return null

  // Build selection label
  const selectionParts: string[] = []
  if (productCount > 0) {
    selectionParts.push(`${productCount} ${t('import.selection.products', 'products')}`)
  }
  if (variantCount > 0) {
    selectionParts.push(`${variantCount} ${t('import.selection.variants', 'variants')}`)
  }
  const selectionLabel = selectionParts.join(', ')

  return createPortal(
    <AnimatePresence>
      <motion.div
        role="region"
        aria-live="polite"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{ duration: 0.24, ease: 'easeOut' }}
        className="fixed top-0 left-0 lg:left-20 right-0 z-40 border-b border-border bg-card shadow-md"
      >
        <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-6 px-4 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              size="lg"
              onClick={onClearSelection}
              className="gap-2"
              aria-label={t('import.selection.clear', { defaultValue: 'Clear selection' })}
            >
              {t('import.selection.clear', { defaultValue: 'Clear selection' })}
              <Kbd>Esc</Kbd>
            </Button>
            <span className="text-sm text-muted-foreground">
              {selectionLabel}
            </span>
          </div>

          <Button
            onClick={onImport}
            disabled={isImporting}
            className="gap-2"
          >
            {isImporting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
            {t('import.actions.import', { count: totalCount, defaultValue: `Add ${totalCount} items` })}
            <Kbd>Enter</Kbd>
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

export default AddFromBaselinkerSelectionActionBar
